import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../../../utills/api";
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Container, Row, Col, Button, Input } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import GlobalPopup from "../../../Components/Common/GlobalPopup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { FiUserCheck } from "react-icons/fi";
import moment from "moment";
import { useNotifications } from "../../../Components/Context/NotificationContext";
import { FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const ManageOrders = () => {
  document.title = "HMS | Food Order";
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("food");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [foodOrders, setFoodOrders] = useState([]);
  const [showItems, setShowItems] = useState(null);
  const [assigningRow, setAssigningRow] = useState(null);
  const [assignedTo, setAssignedTo] = useState({});
  const [members, setMembers] = useState([]);
  const { addNotification } = useNotifications();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [posOrders, setPosOrders] = useState([]);
  const lastFoodCount = useRef(0);
  const lastDineCount = useRef(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const API_BASE = "food";
  const API_URL = "team";

  const audioRef = useRef(new Audio("/notification.mp3"));

  const playSound = () => {
    try {
      audioRef.current.play().catch(() => {
        console.warn("User interaction required to play sound");
      });
    } catch (err) {
      console.error("Error playing notification sound:", err);
    }
  };

  // Fetch Food Orders
  useEffect(() => {
    let isInitialLoad = true;
    const fetchFoodOrders = async () => {
      try {
        const res = await axios.get(`${API_BASE}/order`);
        const data = Array.isArray(res.data) ? res.data : [];

        // Skip notification for initial load
        if (!isInitialLoad && data.length > lastFoodCount.current) {
          const newCount = data.length - lastFoodCount.current;
          setPopupMessage(`You have ${newCount} new food order${newCount > 1 ? "s" : ""}.`);
          setPopupVisible(true);

          addNotification({
            title: "New Food Orders",
            message: `${newCount} new food order${newCount > 1 ? "s" : ""} received.`,
            type: "info",
          });
          playSound();
        }

        setFoodOrders(data);
        lastFoodCount.current = data.length;
        isInitialLoad = false;
      } catch (err) {
        toast.error("Failed to fetch food orders");
      }
    };

    fetchFoodOrders();
    const interval = setInterval(fetchFoodOrders, 5000);
    return () => clearInterval(interval);
  }, [addNotification]);

  useEffect(() => {
    const fetchPosOrders = async () => {
      try {
        const res = await axios.get("pos/kot");
        setPosOrders(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchPosOrders();
    const interval = setInterval(fetchPosOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  // --- DELETE HANDLER ---
  const handleDelete = async (row, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type} record?`))
      return;

    try {
      await axios.delete(`${API_BASE}/${type}/${row._id}`);
      toast.success(`${type} booking deleted successfully`);

      if (type === "order") {
        setFoodOrders(prev => prev.filter(item => item._id !== row._id));
      } else if (type === "dine") {
        setDineBookings(prev => prev.filter(item => item._id !== row._id));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete record");
    }
  };

  // all orders table data
  const allOrders = useMemo(() => {
    const roomOrders = foodOrders.map((o) => ({
      ...o,
      source: "ROOM_SERVICE",
      displayStatus:
        o.chefStatus && o.chefStatus !== "Waiting"
          ? o.chefStatus
          : o.status,
    }));

    const kotOrders = posOrders.map((o) => ({
      _id: o._id,
      bookingId: o.kotNumber,
      kotNumber: o.kotNumber,
      roomNumber: o.roomNumber || o.tableNo || "-",
      items: o.items || [],
      totalAmount: o.grandTotal,
      finalAmount: o.grandTotal,
      status: o.status,
      displayStatus: o.status,
      createdAt: o.createdAt,
      source: o.roomNumber ? "ROOM_DELIVERY" : "POS",
    }));

    return [...roomOrders, ...kotOrders].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [foodOrders, posOrders]);

  const orderCounts = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");

    return {
      ALL: allOrders.length,

      TODAY: allOrders.filter(
        (o) =>
          moment(o.createdAt).format("YYYY-MM-DD") ===
          today
      ).length,

      DELIVERED: allOrders.filter(
        (o) =>
          (o.displayStatus || o.status)?.toLowerCase() === "delivered"
      ).length,

      PREPARING: allOrders.filter(
        (o) =>
          (o.displayStatus || o.status)?.toLowerCase() === "preparing"
      ).length,

      PENDING: allOrders.filter(
        (o) =>
          (o.displayStatus || o.status)?.toLowerCase() === "pending"
      ).length,

      CANCELLED: allOrders.filter(
        (o) =>
          (o.displayStatus || o.status)?.toLowerCase() === "cancelled"
      ).length,
    };
  }, [allOrders]);

  const filteredOrders = useMemo(() => {
    let data = [...allOrders];

    // Existing status tabs
    switch (selectedStatus) {
      case "TODAY":
        data = data.filter(
          (o) =>
            moment(o.createdAt).format(
              "YYYY-MM-DD"
            ) ===
            moment().format("YYYY-MM-DD")
        );
        break;

      case "DELIVERED":
        data = data.filter(
          (o) =>
            (o.displayStatus || o.status)?.toLowerCase() ===
            "delivered"
        );
        break;

      case "PREPARING":
        data = data.filter(
          (o) =>
            (o.displayStatus || o.status)?.toLowerCase() ===
            "preparing"
        );
        break;

      case "PENDING":
        data = data.filter(
          (o) =>
            (o.displayStatus || o.status)?.toLowerCase() ===
            "pending"
        );
        break;

      case "CANCELLED":
        data = data.filter(
          (o) =>
            (o.displayStatus || o.status)?.toLowerCase() ===
            "cancelled"
        );
        break;

      default:
        break;
    }

    // Search filter
    if (search.trim()) {
      const q =
        search.toLowerCase();

      data = data.filter(
        (o) =>
          (o.bookingId || "")
            .toLowerCase()
            .includes(q) ||
          (o.roomNumber || "")
            .toLowerCase()
            .includes(q) ||
          (o.guestName || "")
            .toLowerCase()
            .includes(q)
      );
    }

    // Dropdown filter
    if (statusFilter !== "ALL") {
      data = data.filter((o) => {
        switch (statusFilter) {
          case "KOT_BOOKING":
            return (
              o.source ===
              "KOT_BOOKING"
            );

          case "ROOM_DELIVERY":
            return (
              o.source ===
              "ROOM_DELIVERY"
            );

          case "DINE_IN":
            return (
              o.source ===
              "DINE_IN"
            );

          case "GUEST_BOOKING":
            return (
              o.source ===
              "GUEST_BOOKING"
            );

          case "Pending":
            return (
              (o.displayStatus || o.status)?.toLowerCase() ===
              "pending"
            );

          case "Preparing":
            return (
              (o.displayStatus || o.status)?.toLowerCase() ===
              "preparing"
            );

          case "Delivered":
            return (
              (o.displayStatus || o.status)?.toLowerCase() ===
              "delivered"
            );

          default:
            return true;
        }
      });
    }

    return data;
  }, [
    allOrders,
    selectedStatus,
    search,
    statusFilter,
  ]);

  const sendToChef = (order) => {

    const orderNumber =
      order.orderId ||
      order.bookingId ||
      order.kotNumber;

    const chefLink =
      `${window.location.origin}/chef-order/${orderNumber}`;

    const message = `
🍽 NEW ORDER

Order ID: ${orderNumber}

Room: ${order.roomNumber || "-"}

Guest: ${order.guestName || "-"}

Amount: ₹${order.finalAmount}

Open Order:
${chefLink}
`;

    window.open(
      `https://wa.me/?text=${encodeURIComponent(
        message
      )}`,
      "_blank"
    );
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Manage Bookings" pageTitle="Bookings" />
          <Row>

            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex flex-wrap gap-2">

                    <Button
                      color={
                        selectedStatus === "ALL"
                          ? "primary"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "ALL"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("ALL")
                      }
                    >
                      All
                      <span className="ms-1 badge bg-primary">
                        {orderCounts.ALL}
                      </span>
                    </Button>

                    <Button
                      color={
                        selectedStatus === "TODAY"
                          ? "primary"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "TODAY"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("TODAY")
                      }
                    >
                      Today Orders
                      <span className="ms-1 badge bg-primary">
                        {orderCounts.TODAY}
                      </span>
                    </Button>

                    <Button
                      color={
                        selectedStatus === "DELIVERED"
                          ? "success"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "DELIVERED"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("DELIVERED")
                      }
                    >
                      Delivered
                      <span className="ms-1 badge bg-success">
                        {orderCounts.DELIVERED}
                      </span>
                    </Button>

                    <Button
                      color={
                        selectedStatus === "PREPARING"
                          ? "warning"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "PREPARING"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("PREPARING")
                      }
                    >
                      Preparing
                      <span className="ms-1 badge bg-warning">
                        {orderCounts.PREPARING}
                      </span>
                    </Button>

                    <Button
                      color={
                        selectedStatus === "PENDING"
                          ? "danger"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "PENDING"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("PENDING")
                      }
                    >
                      Pending
                      <span className="ms-1 badge bg-danger">
                        {orderCounts.PENDING}
                      </span>
                    </Button>

                    <Button
                      color={
                        selectedStatus === "CANCELLED"
                          ? "secondary"
                          : "dark"
                      }
                      outline={
                        selectedStatus !== "CANCELLED"
                      }
                      size="sm"
                      onClick={() =>
                        setSelectedStatus("CANCELLED")
                      }
                    >
                      Cancelled
                      <span className="ms-1 badge bg-secondary">
                        {orderCounts.CANCELLED}
                      </span>
                    </Button>

                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    style={{
                      height: "32px",
                      minWidth: "160px",
                    }}
                    onClick={() =>
                      navigate("/create-order")
                    }
                  >
                    Create Order
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>

                <Row className="mb-4 align-items-center">
                  <Col md={6}>
                    <Input
                      type="text"
                      placeholder="Search by Order ID, Room No, Guest..."
                      value={search}
                      onChange={(e) =>
                        setSearch(e.target.value)
                      }
                    />
                  </Col>

                  <Col md={6}>
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(
                          e.target.value
                        )
                      }
                    >
                      <option value="ALL">
                        All Orders
                      </option>

                      <option value="KOT_BOOKING">
                        KOT Booking
                      </option>

                      <option value="ROOM_DELIVERY">
                        Room Delivery
                      </option>

                      <option value="DINE_IN">
                        Dine In
                      </option>

                      <option value="Pending">
                        Pending
                      </option>

                      <option value="Preparing">
                        Ongoing
                      </option>

                      <option value="Delivered">
                        Delivered
                      </option>

                      <option value="GUEST_BOOKING">
                        Guest Booking
                      </option>
                    </Input>
                  </Col>
                </Row>

                <Row className="g-3">
                  {filteredOrders.map((order) => (
                    <Col
                      key={order._id}
                      xxl={3}
                      xl={3}
                      lg={4}
                      md={6}
                      sm={12}
                      xs={12}
                    >
                      <Card
                        style={{
                          border: "1px solid #e9ecef",
                          borderRadius: "8px",
                          height: "300px",
                        }}
                      >
                        <CardBody className="d-flex flex-column"
                          style={{ height: "100%" }}>
                          {/* Header */}
                          <div className="d-flex justify-content-between mb-3">
                            <div>
                              <small className="text-muted">Order ID</small>
                              <div className="fw-semibold">
                                {order.bookingId}
                              </div>
                            </div>

                            <div
                              className="text-end"
                              style={{ fontSize: "12px" }}
                            >
                              {moment(order.createdAt).format(
                                "DD MMM YYYY"
                              )}
                              <br />
                              {moment(order.createdAt).format(
                                "hh:mm A"
                              )}
                            </div>
                          </div>

                          {/* Room + Type */}
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <small className="text-muted">
                                Room No
                              </small>
                              <div className="fw-semibold">
                                {order.roomNumber || "-"}
                              </div>
                            </div>

                            <span
                              style={{
                                background: "#e8f8ee",
                                color: "#198754",
                                padding: "5px 12px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: "600"
                              }}
                            >
                              {order.source}
                            </span>
                          </div>

                          <hr className="my-2" />

                          {/* Items */}
                          <div
                            style={{
                              flex: 1,
                              overflowY: "auto",
                              minHeight: 0,
                            }}
                          >

                   {(order.items || []).map((item, idx) => (
  <div
    key={idx}
    className="d-flex justify-content-between align-items-center py-1"
  >
    <div>
      <div style={{ fontWeight: 600 }}>
        {typeof item.name === "string"
          ? item.name
          : item.name?.en}
      </div>

      {typeof item.name === "object" &&
        item.name?.hi && (
          <small
            className="text-muted d-block"
          >
            {item.name.hi}
          </small>
        )}
    </div>

    <div>x{item.quantity}</div>

    <div>
      ₹{item.total || item.price}
    </div>
  </div>
))}
</div>


                          <hr />

                          {/* Total */}
                          <div className="d-flex justify-content-between mb-3">
                            <span>Total Payable</span>

                            <strong>
                              ₹{order.finalAmount}
                            </strong>
                          </div>

                          {/* Footer */}
                          <div className="d-flex justify-content-between align-items-center">

                            <button
                              className="btn btn-sm"
                              style={{
                                background: "#fff",
                                border: `1px solid ${order.displayStatus === "Delivered"
                                  ? "#198754"
                                  : order.displayStatus === "Preparing"
                                    ? "#ffc107"
                                    : order.displayStatus === "Pending"
                                      ? "#dc3545"
                                      : order.displayStatus === "Cancelled"
                                        ? "#6c757d"
                                        : "#0d6efd"
                                  }`,
                                color:
                                  order.displayStatus === "Delivered"
                                    ? "#198754"
                                    : order.displayStatus === "Preparing"
                                      ? "#ffc107"
                                      : order.displayStatus === "Pending"
                                        ? "#dc3545"
                                        : order.displayStatus === "Cancelled"
                                          ? "#6c757d"
                                          : "#0d6efd",
                                fontWeight: 600,
                              }}
                            >
                              {order.displayStatus}
                            </button>
                            <div className="d-flex gap-2">

                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => sendToChef(order)}
                              >
                                <FaWhatsapp />
                              </button>

                              <button
                                className="btn btn-sm btn-soft-danger"
                                onClick={() =>
                                  handleDelete(order, "order")
                                }
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>

                            </div>

                          </div>

                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>

              </CardBody>
              <GlobalPopup
                show={popupVisible}
                message={popupMessage}
                onClose={() => setPopupVisible(false)}
              />

            </Card>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManageOrders;
